import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/ui/button";
import { useI18nContext } from "@i18n/i18n-react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { formatToCurrency } from "@utils/currency";
import { Minus, Plus } from "lucide-react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { useCart } from "~/contexts/CartContext";
import { ProductCardFragment$key } from "./__generated__/ProductCardFragment.graphql";
import { type KeyboardEvent, type MouseEvent, type ReactNode, useMemo } from "react";
import { getDiscountFraction, isDiscountName, stripDiscountSuffix } from "@utils/discount";

const fragment = graphql`
	fragment ProductCardFragment on HubProduct {
		id
		priceAmount
		product {
			id
			name
			description
			photo {
				id
				variant(variant: PUBLIC)
			}
			brand {
				id
				name
			}
			category {
				id
				name
				i18N
			}
		}
		stock
		outOfStock
		priceAmount
	}
`;

function CardWrapper({
	id,
	onProductClick,
	children,
}: {
	id: string;
	onProductClick?: (hubProductId: string) => void;
	children: ReactNode;
}) {
	const navigate = useNavigate();
	const { hubProductId } = useSearch({ strict: false });

	const handleClick = () => {
		if (onProductClick) {
			onProductClick(id);
			return;
		}

		void navigate({
			from: "/$hubMetaName/instant-buy/",
			search: { hubProductId: id },
			replace: hubProductId !== undefined,
		});
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== "Enter" && event.key !== " ") {
			return;
		}

		event.preventDefault();
		handleClick();
	};

	return (
		<div
			role="link"
			tabIndex={0}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			className="h-full cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
		>
			{children}
		</div>
	);
}

type ProductCardProps = {
	product: ProductCardFragment$key;
	discount?: number;
	showAddButton?: boolean;
	onProductClick?: (hubProductId: string) => void;
};

export const ProductCard = (props: ProductCardProps) => {
	const { showAddButton = true, onProductClick } = props;
	const { id, product, priceAmount, outOfStock, stock } = useFragment(fragment, props.product);
	const useCartStore = useCart();
	const i18n = useI18nContext().LL["new-ui"];

	const addToCart = useCartStore((state) => state.addToCart);
	const updateQuantity = useCartStore((state) => state.updateQuantity);
	const cartItem = useCartStore((state) => state.items.find((item) => item.id === id));
	const maxQuantity = stock > 1 && priceAmount === 0 ? 1 : stock;

	const ogPrice = useMemo(() => {
		if (isDiscountName(product.name)) {
			const discountFrac = getDiscountFraction(product.name) ?? 1;
			return priceAmount / (1 - discountFrac);
		}

		return priceAmount;
	}, [priceAmount, product]);

	const ogName = useMemo(() => stripDiscountSuffix(product.name), [product]);
	const isDiscounted = isDiscountName(product.name);

	const stopCardNavigation = (event: MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleAdd = (event: MouseEvent<HTMLElement>) => {
		stopCardNavigation(event);
		addToCart(id, priceAmount, 1, maxQuantity);
	};

	const handleIncrement = (event: MouseEvent<HTMLElement>) => {
		stopCardNavigation(event);
		if (!cartItem) {
			addToCart(id, priceAmount, 1, maxQuantity);
			return;
		}

		updateQuantity(id, Math.min(cartItem.quantity + 1, maxQuantity));
	};

	const handleDecrement = (event: MouseEvent<HTMLElement>) => {
		stopCardNavigation(event);
		if (!cartItem) {
			return;
		}

		updateQuantity(id, cartItem.quantity - 1);
	};

	return (
		<CardWrapper id={id} onProductClick={onProductClick}>
			<div className="flex h-full w-full flex-col gap-4">
				<div className="relative aspect-square overflow-hidden rounded-xl bg-muted shadow-sm">
					<img
						src={product.photo?.variant ?? "https://ui.shadcn.com/placeholder.svg"}
						alt={ogName}
						className={cn("h-full w-full object-cover", outOfStock && "opacity-50")}
						loading="lazy"
					/>

					{outOfStock && (
						<span className="absolute left-2 top-2 inline-flex items-center rounded-full border bg-background px-2 py-1 text-xs font-medium text-foreground shadow-sm font-breit">
							{i18n.OUT_OF_STOCK()}
						</span>
					)}

					{showAddButton && !outOfStock && !cartItem && (
						<div className="absolute bottom-2 right-2" onClick={stopCardNavigation} onPointerDown={(event) => event.stopPropagation()}>
							<Button
								variant="outline"
								size="icon"
								onClick={handleAdd}
								className="h-9 w-9 rounded-md bg-background"
								aria-label={`Add ${ogName}`}
							>
								<Plus className="h-4 w-4" />
							</Button>
						</div>
					)}

					{showAddButton && !outOfStock && cartItem && (
						<div
							className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-input bg-background p-2"
							onClick={stopCardNavigation}
							onPointerDown={(event) => event.stopPropagation()}
						>
							<Button
								variant="outline"
								size="icon"
								onClick={handleDecrement}
								className="h-9 w-9 rounded-md"
								aria-label={`Remove ${ogName}`}
							>
								<Minus className="h-4 w-4" />
							</Button>

							<span className="min-w-6 px-2 text-center text-sm leading-5 text-muted-foreground" aria-live="polite">
								{cartItem.quantity}
							</span>

							<Button
								variant="outline"
								size="icon"
								onClick={handleIncrement}
								disabled={cartItem.quantity >= maxQuantity}
								className="h-9 w-9 rounded-md"
								aria-label={`Add another ${ogName}`}
							>
								<Plus className="h-4 w-4" />
							</Button>
						</div>
					)}
				</div>

				<div className="flex flex-1 flex-col gap-2 px-2">
					<div className="flex flex-1 flex-col gap-1.5">
						<div className="truncate font-breit text-sm font-medium leading-none text-muted-foreground">{product.brand?.name ?? " "}</div>

						<div className="truncate font-breit text-base font-semibold leading-none text-foreground">{ogName}</div>

						<div className="min-h-[40px] line-clamp-2 text-sm leading-5 text-muted-foreground">{product.description ?? " "}</div>
					</div>

					<div className="flex flex-wrap items-center gap-x-1 gap-y-2">
						<div className="font-breit text-base font-medium leading-6 text-foreground">{formatToCurrency(priceAmount)}</div>

						{isDiscounted ? (
							<div className="font-breit text-base font-normal leading-6 text-muted-foreground line-through">
								{formatToCurrency(ogPrice)}
							</div>
						) : null}
					</div>
				</div>
			</div>
		</CardWrapper>
	);
};
